import { useEffect, useRef, useState } from 'react';
import { StatusBadge } from '@/components/base/StatusBadge';
import type { FarmerKycInfo } from '@/store';
import type { Contract } from '@/types';
import { formatCompactCurrency, formatDate } from '@/utils/format';
import { getKycLabel } from '@/utils/status';

interface ContractCardProps {
  contract: Contract;
  farmerName: string;
  farmerId: string;
  farmerKyc?: FarmerKycInfo;
  seasonNames: string[];
  cayTrong?: string;
  onViewDetail: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onAddDocs?: () => void;
  onExtend?: () => void;
}

export default function ContractCard({
  contract,
  farmerName,
  farmerKyc,
  seasonNames,
  cayTrong,
  onViewDetail,
  onEdit,
  onDelete,
  onAddDocs,
  onExtend,
}: ContractCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const isFarmerContract = contract.loaiHopDong === 'HTX-Nông hộ';
  const allocated = contract.hanMucDaCap || 0;
  const remaining = Math.max(0, contract.hanMucConLai ?? contract.giaTri - allocated);
  const usedPercent = contract.giaTri > 0 ? Math.min(100, allocated / contract.giaTri * 100) : 0;

  return (
    <article className="mobile-card min-w-0 overflow-visible p-3.5">
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={onViewDetail} className="min-w-0 flex-1 text-left">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="truncate text-sm font-bold text-slate-900">{contract.soHopDong}</h3>
            <StatusBadge status={contract.status} />
            {contract.expiringSoon && <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700">Sắp hết hạn</span>}
          </div>
          <p className="mt-1 truncate text-xs font-medium text-slate-700">{contract.doiTac || farmerName}</p>
          <p className="metric-caption mt-0.5 truncate text-[10px] text-slate-400">
            {isFarmerContract ? 'Bao tiêu nông hộ' : contract.loaiHopDong}
            {cayTrong ? ` · ${cayTrong}` : ''}
          </p>
        </button>

        {(onEdit || onDelete || onAddDocs || onExtend) && (
          <div ref={menuRef} className="relative shrink-0">
            <button type="button" onClick={() => setMenuOpen((value) => !value)} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-50 text-slate-500" aria-label="Tùy chọn hợp đồng">
              <i className="ri-more-2-fill" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-10 z-30 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
                {onEdit && <MenuButton icon="ri-edit-line" label="Sửa hợp đồng" onClick={onEdit} close={() => setMenuOpen(false)} />}
                {onAddDocs && <MenuButton icon="ri-attachment-2" label="Quản lý tài liệu" onClick={onAddDocs} close={() => setMenuOpen(false)} />}
                {onExtend && <MenuButton icon="ri-calendar-check-line" label="Gia hạn" onClick={onExtend} close={() => setMenuOpen(false)} />}
                {onDelete && <MenuButton icon="ri-delete-bin-line" label="Xóa hợp đồng" onClick={onDelete} close={() => setMenuOpen(false)} danger />}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <MiniMetric label="Giá trị" value={formatCompactCurrency(contract.giaTri)} />
        <MiniMetric label="Đã cấp" value={formatCompactCurrency(allocated)} tone="amber" />
        <MiniMetric label="Còn lại" value={formatCompactCurrency(remaining)} tone="emerald" />
      </div>

      {isFarmerContract && (
        <div className="mt-3">
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-agrida-500" style={{ width: `${usedPercent}%` }} />
          </div>
          <p className="metric-caption mt-1 text-[10px] text-slate-400">Đã phân bổ {usedPercent.toFixed(0)}% giá trị hợp đồng</p>
        </div>
      )}

      {expanded && (
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl bg-slate-50 p-3 text-xs">
          <Detail label="Hiệu lực đến" value={formatDate(contract.ngayHetHan)} />
          <Detail label="Tài liệu" value={`${contract.attachments?.length || 0} tệp`} />
          <Detail label="eKYC" value={farmerKyc ? getKycLabel(farmerKyc.kycStatus) : 'Chưa có'} />
          <Detail label="Mức rủi ro" value={farmerKyc?.riskLevel || 'Chưa đánh giá'} />
          {seasonNames.length > 0 && <div className="col-span-2"><Detail label="Mùa vụ" value={seasonNames.join(', ')} /></div>}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
        <button type="button" onClick={onViewDetail} className="min-h-11 flex-1 rounded-xl bg-slate-100 px-3 text-xs font-semibold text-slate-700">Chi tiết</button>
        <button type="button" onClick={() => setExpanded((value) => !value)} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500" aria-label={expanded ? 'Thu gọn' : 'Xem thêm'}>
          <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        </button>
      </div>
    </article>
  );
}

function MiniMetric({ label, value, tone = 'slate' }: { label: string; value: string; tone?: 'slate' | 'amber' | 'emerald' }) {
  const colors = { slate: 'text-slate-800', amber: 'text-amber-700', emerald: 'text-agrida-700' };
  return <div className="min-w-0 rounded-xl bg-slate-50 p-2.5 text-center"><p className="text-[10px] text-slate-400">{label}</p><p className={`mt-0.5 truncate text-xs font-bold ${colors[tone]}`}>{value}</p></div>;
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0"><p className="text-[10px] text-slate-400">{label}</p><p className="mt-0.5 text-xs font-medium text-slate-700 text-safe-normal">{value}</p></div>;
}

function MenuButton({ icon, label, onClick, close, danger = false }: { icon: string; label: string; onClick: () => void; close: () => void; danger?: boolean }) {
  return <button type="button" onClick={() => { close(); onClick(); }} className={`flex min-h-10 w-full items-center gap-2 px-3 text-left text-xs ${danger ? 'text-rose-600' : 'text-slate-700'}`}><i className={icon} />{label}</button>;
}
